import { useEffect, useMemo, useState, Suspense } from 'react';
import { lazyWithReload as lazy } from './utils/staleBuild';
import type { ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { RouteOriginTracker } from './utils/routeOrigin';
import { PageViewTracker } from './utils/telemetry';
import AppUpdateOnNavigation from './components/AppUpdateOnNavigation';
import KeyedByRouteParam from './components/common/KeyedByRouteParam';
import { AuthProvider } from 'react-oidc-context';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { ItemSavedListener } from './pages/items/itemEditorLink';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';

import { getUserManager, onSigninCallback } from './auth/authConfig';
import { installQueryPollingGate, isSignedOutNoise } from './auth/pollingGate';
import AuthGuard from './auth/AuthGuard';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingScreen from './components/common/LoadingScreen';
import DatabaseUpdateGate from './components/common/DatabaseUpdateGate';
import SecureRoute from './components/common/SecureRoute';
import PsiOnlyRoute from './components/common/PsiOnlyRoute';
import PurchasingLicenseRoute from './components/common/PurchasingLicenseRoute';
import PurchasingFeatureRoute from './components/common/PurchasingFeatureRoute';
import DesktopOrTabletOnly, { MENU_ANALYSIS_PHONE_LINE } from './components/common/DesktopOrTabletOnly';
import type { PurchasingFeature } from './api/purchasing/features';
import { USERS_MANAGED_IN_TECH_LOG } from './utils/psiStaff';
import { LEVEL } from './hooks/usePermission';
import { useGlobalStore } from './stores/globalStore';
import { useConnectionStore } from './stores/connectionStore';
import { resolveStartRoute } from './auth/resolveStartRoute';
import { clearReturnPath, peekReturnPath, startTargetWithReturn } from './auth/returnAfterSignIn';
import { useUser } from './api/usersApi';
import { useRole } from './api/rolesApi';
import NotificationToast from './components/common/NotificationToast';
import LiveAnnouncer from './components/common/LiveAnnouncer';
import PageAnnouncer from './components/common/PageAnnouncer';
import ScreenSummaryAnnouncer from './components/common/ScreenSummary';
import ApiDownOverlay from './components/common/ApiDownOverlay';
import FacilitySwitchOverlay from './components/common/FacilitySwitchOverlay';
import SessionExpiredDialog from './components/common/SessionExpiredDialog';
import VersionMismatchDialog from './components/common/VersionMismatchDialog';
import SessionExpiryWarning from './components/common/SessionExpiryWarning';
import { useThemeStore } from './stores/themeStore';
import { buildMuiTheme } from './theme/muiTheme';

// Layouts — kept eager (always needed)
import MainLayout from './components/layout/MainLayout';
import EmptyLayout from './components/layout/EmptyLayout';
import { movedMenuAddress } from './navigation/menuPages';

// Dashboard — eagerly loaded (first page most users see)
import Dashboard from './pages/Dashboard';

// Lazy fallback spinner
function LazyFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <CircularProgress size={36} />
    </div>
  );
}

// ============================================================
// Lazy-loaded pages — route-based code splitting
// ============================================================

// Connection / Overview
const ConnectionSelection = lazy(() => import('./pages/ConnectionSelection'));
const CorporateDashboard = lazy(() => import('./pages/corporate-dashboard/CorporateDashboard'));
const SectionRedirect = lazy(() => import('./navigation/SectionRedirect'));
const SectionHome = lazy(() => import('./navigation/SectionHome'));

// People
// The original grid and toolbar, kept at /people/classic during internal
// testing. Delete with PeoplePage.tsx once the rebuild has bedded in.
const PeoplePage = lazy(() => import('./pages/people/PeoplePage'));
const PeoplePageNext = lazy(() => import('./pages/people/PeoplePageNext'));
const ManageDeleted = lazy(() => import('./pages/people/ManageDeleted'));
const ResidentDetail = lazy(() => import('./pages/people/ResidentDetail'));
const GuestProfile = lazy(() => import('./pages/people/GuestProfile'));
const Guests = lazy(() => import('./pages/people/Guests'));

// Menu Cycle
const MenuCycles = lazy(() => import('./pages/menu-cycle/MenuCycles'));
// Menu Cycle editor: "Modify" on the Menu Cycles list. Since 2026-10-13 it is
// the 2026-10 redesign that was built as "Modify (Beta)" (pages/menu-cycle-beta),
// and /menucycle/beta/:id redirects here. The Workbench it was copied from
// (pages/menu-cycle-redesign/MenuCycleWorkbench) and the classic
// MenuCycleModifyDialog were deleted on 2026-10-17. See the header comment in
// MenuCycleWorkbench.tsx.
const MenuCycleWorkbench = lazy(() => import('./pages/menu-cycle-beta/MenuCycleWorkbench'));
const BasicMenuCycleEditor = lazy(() => import('./pages/menu-cycle/basic/BasicMenuCycleEditor'));
// Today's menu, read-only: what a phone gets under Menus (menu editing is closed on phones).
const TodaysMenuPage = lazy(() => import('./pages/menu-cycle/today/TodaysMenuPage'));
// Import a vendor menu (beta): parse a dietitian's seasonal package and match its items.
// Menus > New Menu (PSI staff only): the vendor import, drafting with AI and what the AI remembers, as tabs.
const NewMenuPage = lazy(() => import('./pages/menu-cycle-redesign/menus/NewMenuPage'));
// Menus > Menu Changes: Ask PSI, From extension sheets and (PSI staff) Change with AI, as tabs.
const MenuChangesPage = lazy(() => import('./pages/menu-cycle-redesign/menus/MenuChangesPage'));
// Nutrition analysis per diet order (beta).
const MenuAnalysisPage = lazy(() => import('./pages/menu-cycle-redesign/analysis/MenuAnalysisPage'));
// Swap meals, categories or items between days ("we didn't get the delivery").
const MenuSwapPage = lazy(() => import('./pages/menu-cycle-redesign/swap/MenuSwapPage'));
const RecycleBin = lazy(() => import('./pages/menu-cycle/RecycleBin'));
const AutoItemMods = lazy(() => import('./pages/menu-cycle/AutoItemMods'));
const ExcelDietOrders = lazy(() => import('./pages/menu-cycle/ExcelDietOrders'));

// Reference Data
const Tables = lazy(() => import('./pages/tables/Tables'));
const Items = lazy(() => import('./pages/items/Items'));
const ItemDetail = lazy(() => import('./pages/items/ItemDetail'));
const UsdaBulkMatcher = lazy(() => import('./pages/items/UsdaBulkMatcher'));
const OrderGuideAssignment = lazy(() => import('./pages/items/OrderGuideAssignment'));
const ItemLinkingPage = lazy(() => import('./pages/items/ItemLinkingPage'));
const Recipes = lazy(() => import('./pages/recipes/Recipes'));
const RecipeDetail = lazy(() => import('./pages/recipes/RecipeDetail'));

// Misc / Operations
const BulkOrder = lazy(() => import('./pages/misc/BulkOrder'));
const Forecasting = lazy(() => import('./pages/misc/Forecasting'));
const DiningRooms = lazy(() => import('./pages/misc/DiningRooms'));
const Employees = lazy(() => import('./pages/misc/Employees'));
const CreditAutoProcess = lazy(() => import('./pages/misc/CreditAutoProcess'));

// Clinical / Nutritional
const IntakeStudies = lazy(() => import('./pages/clinical-nutrition/IntakeStudies'));
const Validation = lazy(() => import('./pages/nutritional/Validation'));
const SpecialEventsPage = lazy(() => import('./pages/nutritional/SpecialEventsPage'));
const TrayLineSetup = lazy(() => import('./pages/nutritional/TrayLineSetup'));
const GlobalAdjustmentsPage = lazy(() => import('./pages/nutritional/GlobalAdjustmentsPage'));
const MealPatterns = lazy(() => import('./pages/nutritional/MealPatterns'));
const GlobalMealPatternsPage = lazy(() => import('./pages/nutritional/GlobalMealPatternsPage'));
const WeightEntry = lazy(() => import('./pages/nutritional/WeightEntry'));

// Analysis
const AnalysisWorkbench = lazy(() => import('./pages/analysis/workbench/AnalysisWorkbench'));
const AnalysisHome = lazy(() => import('./pages/analysis/workbench/AnalysisWorkbench').then((m) => ({ default: m.AnalysisHome })));
const NutritionalAnalysisEnhanced = lazy(() => import('./pages/analysis/NutritionalAnalysisEnhanced'));
const CostAnalysisMenu = lazy(() => import('./pages/analysis/CostAnalysisMenu'));
const CostAnalysisNourishment = lazy(() => import('./pages/analysis/CostAnalysisNourishment'));
const DemandForecasting = lazy(() => import('./pages/analysis/DemandForecasting'));

// Food Data / Reports / Workflows
const Reports = lazy(() => import('./pages/reports/Reports'));
const Workflows = lazy(() => import('./pages/workflows/Workflows'));

// Tools
const Facilities = lazy(() => import('./pages/tools/Facilities'));
const AdminStatusPage = lazy(() => import('./pages/admin-status/AdminStatusPage'));
const FeedbackAdminPage = lazy(() => import('./pages/admin-feedback/FeedbackAdminPage'));
const DietechDataUpdatePage = lazy(() => import('./pages/settings/DietechDataUpdatePage'));
const FacilityUnits = lazy(() => import('./pages/tools/FacilityUnits'));
const PrinterManagement = lazy(() => import('./pages/tools/PrinterManagement'));
const PrintSchedules = lazy(() => import('./pages/tools/PrintSchedules'));
const UnitRooms = lazy(() => import('./pages/tools/UnitRooms'));
const FacilityCensus = lazy(() => import('./pages/tools/FacilityCensus'));
const DietConsistencyConfig = lazy(() => import('./pages/tools/DietConsistencyConfig'));

// Users / Roles
const UserList = lazy(() => import('./pages/users/UserList'));
const DeletedUsers = lazy(() => import('./pages/users/DeletedUsers'));
const UserDetail = lazy(() => import('./pages/users/UserDetail'));
const CreateUser = lazy(() => import('./pages/users/CreateUser'));
const LoggedInUsers = lazy(() => import('./pages/users/LoggedInUsers'));
const RoleList = lazy(() => import('./pages/roles/RoleList'));
const RoleDetail = lazy(() => import('./pages/roles/RoleDetail'));
const RoleUsage = lazy(() => import('./pages/roles/RoleUsage'));

// EMR / Settings
const EmrPage = lazy(() => import('./pages/emr/EmrPage'));
const FacilitySettings = lazy(() => import('./pages/settings/FacilitySettings'));
const UserSettings = lazy(() => import('./pages/settings/UserSettings'));

// Clinical
const AssessmentList = lazy(() => import('./pages/clinical/AssessmentList'));
const AssessmentWizardPage = lazy(() => import('./pages/clinical/assessmentWizard/AssessmentWizardPage'));
const AssessmentEditPage = lazy(() => import('./pages/clinical/assessmentWizard/edit/AssessmentEditPage'));
const PlanOfCareList = lazy(() => import('./pages/clinical/PlanOfCareList'));
const PlanOfCareRedirect = lazy(() => import('./pages/clinical/planOfCareWizard/PlanOfCareRedirect'));
const PlanOfCareWizardPage = lazy(() => import('./pages/clinical/planOfCareWizard/PlanOfCareWizardPage'));
const PlanOfCareConfiguration = lazy(() => import('./pages/clinical/PlanOfCareConfiguration'));

// Nutrition Compliance (formerly PDPM)
const PdpmDashboard = lazy(() => import('./pages/pdpm/PdpmDashboard'));

// Selective Touch
const SelectiveTouch = lazy(() => import('./pages/selective-touch/SelectiveTouch'));
const TabletApp = lazy(() => import('./pages/tablet/TabletApp'));

// Purchasing (layout: docs/roadmap/purchasing-rebuild.md)
const PurchasingHub = lazy(() => import('./pages/purchasing/PurchasingHub'));
// Ordering
const PurchaseOrders = lazy(() => import('./pages/purchasing/PurchaseOrders'));
const PurchaseOrderDetail = lazy(() => import('./pages/purchasing/PurchaseOrderDetail'));
const OrderGuides = lazy(() => import('./pages/purchasing/OrderGuides'));
const Reorder = lazy(() => import('./pages/purchasing/Reorder'));
const Requisitions = lazy(() => import('./pages/purchasing/Requisitions'));
const RequisitionDetail = lazy(() => import('./pages/purchasing/RequisitionDetail'));
const DemandPlanning = lazy(() => import('./pages/purchasing/DemandPlanning'));
// Receiving & invoices
const Receiving = lazy(() => import('./pages/purchasing/Receiving'));
const ReceivingDetail = lazy(() => import('./pages/purchasing/ReceivingDetail'));
const VendorInvoices = lazy(() => import('./pages/purchasing/VendorInvoices'));
const VendorInvoiceDetail = lazy(() => import('./pages/purchasing/VendorInvoiceDetail'));
const CreditMemos = lazy(() => import('./pages/purchasing/CreditMemos'));
const CreditMemoDetail = lazy(() => import('./pages/purchasing/CreditMemoDetail'));
// Inventory & waste
const InventoryDashboard = lazy(() => import('./pages/purchasing/InventoryDashboard'));
const InventoryItems = lazy(() => import('./pages/purchasing/InventoryItems'));
const InventoryItemDetail = lazy(() => import('./pages/purchasing/InventoryItemDetail'));
const InventoryTransactions = lazy(() => import('./pages/purchasing/InventoryTransactions'));
const InventoryCounts = lazy(() => import('./pages/purchasing/InventoryCounts'));
const InventoryCountDetail = lazy(() => import('./pages/purchasing/InventoryCountDetail'));
const FloorStockPage = lazy(() => import('./pages/purchasing/FloorStockPage'));
const InventoryPlanning = lazy(() => import('./pages/purchasing/InventoryPlanning'));
const Waste = lazy(() => import('./pages/purchasing/Waste'));
// Suppliers & pricing
const SupplierDetail = lazy(() => import('./pages/purchasing/SupplierDetail'));
const ItemLinks = lazy(() => import('./pages/purchasing/ItemLinks'));
const Contracts = lazy(() => import('./pages/purchasing/Contracts'));
const ContractDetail = lazy(() => import('./pages/purchasing/ContractDetail'));
const PriceUpdates = lazy(() => import('./pages/purchasing/PriceUpdates'));
const ContactManager = lazy(() => import('./pages/purchasing/ContactManager'));
// The desktop's `tblOP_Department` / `tblOP_Ledger` lists. /ordering/setup's
// Departments and Ledgers tabs edit the web module's own `tblPur_Department` /
// `tblPur_Ledger` instead (Jeff, 2026-10-24), so these routes are where these
// two components are reached.
const OPDepartments = lazy(() => import('./pages/purchasing/Departments'));
const OPLedgers = lazy(() => import('./pages/purchasing/Ledgers'));
const Suppliers = lazy(() => import('./pages/purchasing/Suppliers'));
const EdiTransactionLog = lazy(() => import('./pages/purchasing/EdiTransactionLog'));
// Costing & analytics
const Costing = lazy(() => import('./pages/purchasing/Costing'));
const PrepSchedule = lazy(() => import('./pages/purchasing/PrepSchedule'));
const AllergenExposure = lazy(() => import('./pages/purchasing/AllergenExposure'));
const SpendAnalytics = lazy(() => import('./pages/purchasing/SpendAnalytics'));
const MealCost = lazy(() => import('./pages/purchasing/MealCost'));
const SupplierScorecard = lazy(() => import('./pages/purchasing/SupplierScorecard'));
const CorporatePurchasing = lazy(() => import('./pages/purchasing/CorporatePurchasing'));
// Setup
const PurchasingSetup = lazy(() => import('./pages/purchasing/PurchasingSetup'));

// Surveys
const SurveyTemplates = lazy(() => import('./pages/surveys/SurveyTemplates'));
const SurveyTemplateDetail = lazy(() => import('./pages/surveys/SurveyTemplateDetail'));
const SurveySessions = lazy(() => import('./pages/surveys/SurveySessions'));
const SurveySessionDetail = lazy(() => import('./pages/surveys/SurveySessionDetail'));
const SurveyResponseEntry = lazy(() => import('./pages/surveys/SurveyResponseEntry'));
const SurveyAnalysis = lazy(() => import('./pages/surveys/SurveyAnalysis'));

// System / Help / AI
const SystemInteractionLog = lazy(() => import('./pages/system-log/SystemInteractionLog'));
const GettingStartedPage = lazy(() => import('./pages/getting-started/GettingStartedPage'));
const AllergyProtectionPage = lazy(() => import('./pages/allergy-protection/AllergyProtectionPage'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));
const HelpArticlePage = lazy(() => import('./pages/help/HelpArticlePage'));
const KitchenHub = lazy(() => import('./pages/kitchen/KitchenHub'));
const NotFound = lazy(() => import('./pages/NotFound'));

// FHIR Integration (public, no auth)
const FhirInfo = lazy(() => import('./pages/fhir/FhirInfo'));
const FhirTerms = lazy(() => import('./pages/fhir/FhirTerms'));
const SmartCallback = lazy(() => import('./pages/fhir/SmartCallback'));
const SmartLaunch = lazy(() => import('./pages/fhir/SmartLaunch'));

// Self-Order (patient-facing, separate auth via SelfOrder JWT)
const SelfOrderApp = lazy(() => import('./pages/self-order/SelfOrderApp').then(m => ({ default: m.SelfOrderApp })));

// Bedside TV / Pillow Speaker (separate layout, no auth)
const BedsideLayout = lazy(() => import('./pages/bedside/BedsideLayout'));
const BedsideScreensaver = lazy(() => import('./pages/bedside/BedsideScreensaver'));
const BedsideWelcome = lazy(() => import('./pages/bedside/BedsideWelcome'));
const BedsidePin = lazy(() => import('./pages/bedside/BedsidePin'));
const BedsideActivation = lazy(() => import('./pages/bedside/BedsideActivation'));
const BedsideTracking = lazy(() => import('./pages/bedside/BedsideTracking'));
const BedsideRating = lazy(() => import('./pages/bedside/BedsideRating'));
const BedsideDiagnostics = lazy(() => import('./pages/bedside/BedsideDiagnostics'));
const BedsideMenuView = lazy(() => import('./pages/bedside/BedsideMenuView'));
const BedsideMenuSelect = lazy(() => import('./pages/bedside/BedsideMenuSelect'));
const BedsideConfirmation = lazy(() => import('./pages/bedside/BedsideConfirmation'));
const BedsideOrderStatus = lazy(() => import('./pages/bedside/BedsideOrderStatus'));
const BedsideGuestTray = lazy(() => import('./pages/bedside/BedsideGuestTray'));
const BedsideSnackMenu = lazy(() => import('./pages/bedside/BedsideSnackMenu'));

import { useNotificationStore } from './stores/notificationStore';
import { ReportViewerProvider } from './contexts/ReportViewerContext';
import { ReportAutoOpenerProvider } from './pages/reports/useReportAutoOpener';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if the mutation already has its own onError handler
      if (mutation.options.onError) return;

      // Signed out: the Session Expired dialog says so. A toast per failed
      // background call (KDS escalations every minute) only adds noise.
      if (isSignedOutNoise(error)) return;

      const status = (error as { response?: { status?: number } })?.response?.status;
      const detail = status ? ` (HTTP ${status})` : '';
      const msg = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;

      useNotificationStore.getState().addNotification(
        msg || `Operation failed${detail}. Please try again.`,
        'error',
      );
    },
  }),
});

// Once the sign-in ends, stop every refetchInterval / focus / reconnect poll
// until the user signs in again (see auth/pollingGate.ts).
installQueryPollingGate();

/** Helper: wraps a page element with SecureRoute */
function secured(securityField: string, feature: string, element: ReactElement, minLevel?: number) {
  return (
    <SecureRoute securityField={securityField} feature={feature} minLevel={minLevel}>
      {element}
    </SecureRoute>
  );
}

/**
 * A page for a role level, or for PSI staff whatever their role allows: the API's
 * RequirePsiStaffOrRoleLevel. The menu entry says so with `psiStaffToo` (navConfig.ts).
 */
function securedOrPsi(securityField: string, feature: string, element: ReactElement, minLevel?: number) {
  return (
    <SecureRoute securityField={securityField} feature={feature} minLevel={minLevel} psiStaffToo>
      {element}
    </SecureRoute>
  );
}

/**
 * A menu editing page: closed on a phone, which gets one line and a way to today's menu
 * (components/common/DesktopOrTabletOnly). The menu cycle list, Swap and the Basic editor
 * (read-only on a phone) stay open.
 */
function notOnPhone(element: ReactElement, message?: string) {
  return <DesktopOrTabletOnly message={message}>{element}</DesktopOrTabletOnly>;
}

/**
 * A purchasing page: the facility must be licensed for purchasing in Tech Log, then the role check,
 * then, for a page outside the core, the optional feature it belongs to must be switched on for the
 * facility in Purchasing Setup (any of several).
 */
/** An old /purchasing address: the same page under /ordering, keeping its query. */
function MovedToOrdering() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`/ordering${pathname.slice('/purchasing'.length)}${search}${hash}`} replace />;
}

function purchasingPage(securityField: string, feature: string, element: ReactElement, purchasingFeature?: PurchasingFeature | PurchasingFeature[]) {
  const page = purchasingFeature ? <PurchasingFeatureRoute feature={purchasingFeature}>{element}</PurchasingFeatureRoute> : element;
  return <PurchasingLicenseRoute>{secured(securityField, feature, page)}</PurchasingLicenseRoute>;
}

/** A user administration page: PSI staff only, then User Management (6) as before. */
function usersPage(element: ReactElement) {
  return (
    <PsiOnlyRoute feature="User Management" message={USERS_MANAGED_IN_TECH_LOG}>
      {secured('user_Level', 'User Management', element, LEVEL.USER_MGMT)}
    </PsiOnlyRoute>
  );
}

/**
 * Reads the current user's `tblPMSec_User.DF_Start_Form` and
 * redirects to the corresponding landing page. Mirrors the WinForms
 * "Startup Form" preference. Waits for the user record to arrive
 * (facility/user IDs are set by AuthTokenSetter after the connection
 * resolves, then `useUser` fetches the row) so we don't flash a
 * fallback page before the real redirect. Falls back to the main
 * patient grid for any value we haven't wired a page for.
 *
 * The role is read here rather than from the global store because
 * AuthTokenSetter swallows a failed role fetch, which would leave this
 * screen waiting forever; the query's error state lets it go on instead.
 */
function StartFormRedirect() {
  const selectedConnection = useConnectionStore((s) => s.selectedConnection);
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const noFacilityAccess = useGlobalStore((s) => s.noFacilityAccess);
  const sessionLoadError = useGlobalStore((s) => s.sessionLoadError);
  const { data: user } = useUser(facilityId, userId);
  const roleId = user?.fKey_PM_Security_Roles ?? 0;
  const { data: roleData, isError: roleFailed } = useRole(facilityId, roleId);
  const role = roleId <= 0 ? {} : roleData ?? (roleFailed ? null : undefined);
  const route = resolveStartRoute({
    hasConnection: !!selectedConnection,
    facilityId,
    userId,
    userLoaded: !!user,
    startForm: user?.dF_Start_Form,
    role,
    noFacilityAccess,
    sessionLoadError,
  });
  // The page the user was on when their sign-in expired (SessionExpiredDialog).
  // Read once; used only once signing in has resolved normally. The connection
  // picker (a different person, or no connection) and a stopped sign-in drop it.
  const [returnTo] = useState(peekReturnPath);
  useEffect(() => {
    if (route.kind === 'redirect') clearReturnPath();
  }, [route.kind]);
  if (route.kind === 'loading') return <LoadingScreen />;
  return <Navigate to={startTargetWithReturn(route.to, returnTo, noFacilityAccess || !!sessionLoadError)} replace />;
}

/** An address a Menus page moved from (navigation/menuPages.ts movedMenuAddress): the tab that holds it now, keeping its query. */
function MovedMenuPage() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${movedMenuAddress(pathname, search) ?? '/menucycle'}${hash}`} replace />;
}

/** Old "Modify (Beta)" links: the beta became Modify, so keep the menu and its query (`?parent=`, day, view). */
function MenuCycleBetaRedirect() {
  const { menuHeaderId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/menucycle/workbench/${menuHeaderId}${search}`} replace />;
}

function AppRouter() {
  return (
    <Suspense fallback={<LazyFallback />}>
    <Routes>
      {/* Auth pages - EmptyLayout (no sidebar) */}
      <Route element={<EmptyLayout />}>
        <Route path="/connection" element={<ConnectionSelection />} />
      </Route>

      {/* Main app - full layout with sidebar. The database update gate sits
          outside MainLayout so none of the layout's own queries run against
          a database that is still being updated. */}
      <Route element={<DatabaseUpdateGate><MainLayout /></DatabaseUpdateGate>}>
        <Route path="/dashboard" element={<Dashboard />} />
        {/* Dietech_Report_Level 1: CorporateDashboardController stages the same rule (cross-facility figures are reporting). */}
        <Route path="/corporate-dashboard" element={secured('dietech_Report_Level', 'Corporate Dashboard', <CorporateDashboard />)} />
        {/* There are no overview pages (Jeff, 2026-10-20): a section opens its main page, or the
            first page in it the user can open (navigation/navConfig.ts). The old hub and overview
            addresses go there too, so bookmarks keep working. */}
        <Route path="/overview" element={<Navigate to="/dashboard" replace />} />
        <Route path="/section/:sectionId" element={<SectionRedirect />} />

        {/* People */}
        {/* The rebuilt grid and toolbar are now what /people serves. They
            were built alongside the original at /people/residents-next so the
            two could be compared; that comparison is done, and leaving the new
            work on a side route meant nobody actually used it.
            The original is still mounted at /people/classic while internal
            testing runs. Static segments, so both rank above /people/:id. */}
        <Route path="/people" element={secured('dietech_Resident_Level', 'Residents', <PeoplePageNext />)} />
        <Route path="/people/classic" element={secured('dietech_Resident_Level', 'Residents', <PeoplePage />)} />
        {/* Deleted residents is a place, not a mode. As a route it gets a
            breadcrumb, a working browser Back, and a URL you can send someone —
            none of which a `showDeleted` boolean provides. Matches the existing
            /users/deleted pattern. */}
        {/* WinForms opens Manage Residents only above Managed Residents 0
            (frmDietech_Resident_DataGrid_V2.vb:945); the API checks the same. */}
        <Route path="/people/deleted" element={secured('dietech_Managed_Residents', 'Deleted Residents', <ManageDeleted />)} />
        {/* Keyed so leaving /people/new for /people/:id (restore by Resident
            ID) mounts a fresh page instead of keeping the new-resident form. */}
        <Route path="/people/new" element={secured('dietech_Resident_Level', 'Residents', <ResidentDetail key="new-resident" />)} />
        {/* Keyed by the id: going from one resident straight to another (the
            top bar search) must load the new resident, not keep showing the
            first one with its form state. */}
        <Route path="/people/:id" element={secured('dietech_Resident_Level', 'Residents', <KeyedByRouteParam param="id"><ResidentDetail /></KeyedByRouteParam>)} />
        {/* Every guest, past and upcoming (the Residents grid lists only
            the last week's and later). Exact path, so it never takes the
            profile routes below. */}
        <Route path="/people/guests" element={secured('dietech_Resident_Level', 'Residents', <Guests />)} />
        {/* Guest profile page (Overview, Primary Info, Schedule,
            Food Allergies, Diet Order). */}
        <Route path="/people/guests/profile/new" element={secured('dietech_Resident_Level', 'Residents', <GuestProfile />)} />
        <Route path="/people/guests/profile/:id" element={secured('dietech_Resident_Level', 'Residents', <GuestProfile />)} />
        {/* The old bulk "Manage Residents" page (Active/Deleted toggle) duplicated the WinForms port at
            /people/deleted and was gated on the wrong role (Jeff, 2026-10-21: "make it like WinForms").
            Old links land on the WinForms-style page. */}
        <Route path="/manage-residents" element={<Navigate to="/people/deleted" replace />} />

        {/* Menu Cycle */}
        <Route path="/menucycle-hub" element={<SectionRedirect section="menus" />} />
        {/* Menu editing is closed on phones (notOnPhone); the list, Swap and the Basic editor
            (read-only there) stay open, and Today's menu is what the phone's Menus tab opens. */}
        <Route path="/menucycle" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', <MenuCycles />)} />
        <Route path="/menucycle/today" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', <TodaysMenuPage />)} />
        <Route path="/menucycle/workbench/:menuHeaderId" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', notOnPhone(<MenuCycleWorkbench />))} />
        {/* Basic (non-Production) facilities: the tblMenu_Detail editor (WinForms frmDietech_Menu_Cycle_Basic). */}
        <Route path="/menucycle/basic/:menuHeaderId" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', <BasicMenuCycleEditor />)} />
        <Route path="/menucycle/beta/:menuHeaderId" element={notOnPhone(<MenuCycleBetaRedirect />)} />
        {/* New Menu: PSI staff only, the vendor import included (Jeff, 2026-11-04; the API's RequirePsiStaff is
            what enforces it). A saved import has its own address; both lines are the same element, so the
            page keeps its state when the first Match saves the import and the address moves. */}
        <Route path="/menucycle/new" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', <PsiOnlyRoute feature="New Menu">{notOnPhone(<NewMenuPage />)}</PsiOnlyRoute>)} />
        <Route path="/menucycle/new/import/:draftId/:stage?" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', <PsiOnlyRoute feature="New Menu">{notOnPhone(<NewMenuPage />)}</PsiOnlyRoute>)} />
        {/* Menu Changes: each tab keeps its page's gate (navigation/menuPages.ts), and the page shows only the tabs the user may open. */}
        <Route path="/menucycle/changes" element={<MenuChangesPage />} />
        {/* The pages Menu Changes and New Menu replaced: old links, pins and Tech Log's links open the right tab, query and all. */}
        <Route path="/menucycle/import" element={<MovedMenuPage />} />
        <Route path="/menucycle/import/:draftId/:stage?" element={<MovedMenuPage />} />
        <Route path="/menucycle/builder" element={<MovedMenuPage />} />
        <Route path="/menucycle/analysis/:menuHeaderId" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', notOnPhone(<MenuAnalysisPage />, MENU_ANALYSIS_PHONE_LINE))} />
        <Route path="/menucycle/swap/:menuHeaderId" element={secured('dietech_Menu_Cycle_Holiday_Overrides_Level', 'Menu Cycle', <MenuSwapPage />, 3)} />
        <Route path="/menucycle/requests" element={<MovedMenuPage />} />
        <Route path="/menucycle/request-change" element={<MovedMenuPage />} />
        {/* The desktop program's "Menu Change Request": old links land on the tab that asks PSI. */}
        <Route path="/menucycle/changerequest" element={<MovedMenuPage />} />
        {/* Menu Cycles' Deleted menus button; no longer in the menu. */}
        <Route path="/menucycle/recyclebin" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', notOnPhone(<RecycleBin />))} />
        <Route path="/menucycle/aim" element={secured('dietech_Menu_Cycle_Level', 'Auto Item Mods', notOnPhone(<AutoItemMods />))} />
        <Route path="/menucycle/excel-diet-orders" element={secured('dietech_Menu_Cycle_Level', 'Menu Cycle', notOnPhone(<ExcelDietOrders />))} />

        <Route path="/food-data" element={<SectionRedirect section="food-data" />} />

        {/* Reference Data */}
        <Route path="/tables/:tableKey?" element={secured('dietech_Table_Level', 'Tables', <Tables />)} />
        <Route path="/items" element={secured('dietech_Items_Level', 'Items', <Items />)} />
        <Route path="/items/new" element={secured('dietech_Items_Level', 'Items', <ItemDetail />, 3)} />
        <Route path="/items/:id" element={secured('dietech_Items_Level', 'Items', <ItemDetail />)} />
        <Route path="/items/:id/edit" element={secured('dietech_Items_Level', 'Items', <ItemDetail />)} />
        <Route path="/items/usda-matcher" element={secured('dietech_Items_Level', 'Items', <UsdaBulkMatcher />)} />
        {/* Items > Linking, listed above Items 0 and not under Assessment Only (frmDietech_Resident_DataGrid_V2.vb:6805-6811); also Tables > Item Linking. */}
        <Route path="/items/linking" element={secured('dietech_Items_Level', 'Item Linking', <ItemLinkingPage />)} />
        {/* Items > Order Guide/Shelf Assignment, listed above Order Guide Assignment 0 (frmDietech_Resident_DataGrid_V2.vb:6813-6816). */}
        <Route path="/items/order-guide-assignment" element={secured('dietech_Purchasing_Order_Guides_Assignment_Level', 'Order Guide/Shelf Assignment', <OrderGuideAssignment />)} />
        <Route path="/recipes" element={secured('dietech_Recipe_Level', 'Recipes', <Recipes />)} />
        <Route path="/recipes/new" element={secured('dietech_Recipe_Level', 'Recipes', <RecipeDetail />)} />
        <Route path="/recipes/:id" element={secured('dietech_Recipe_Level', 'Recipes', <RecipeDetail />)} />
        <Route path="/recipes/:id/edit" element={secured('dietech_Recipe_Level', 'Recipes', <RecipeDetail />)} />

        {/* Miscellaneous */}
        <Route path="/operations" element={<SectionRedirect section="kitchen" />} />
        <Route path="/bulk-order" element={secured('dietech_Menu_Cycle_Level', 'Bulk Order', <BulkOrder />)} />
        {/* ucDietech_Prd_Forecasting.vb: listed above Dietech_Forecasting_Level 0 (V2.vb:6791). */}
        <Route path="/forecasting" element={secured('dietech_Forecasting_Level', 'Forecasting', <Forecasting />)} />
        <Route path="/dining-rooms" element={secured('dietech_Dining_Room_Layout_Level', 'Dining Rooms', <DiningRooms />)} />
        <Route path="/employees" element={secured('dietech_Employee_Level', 'Employees', <Employees />)} />
        <Route path="/credit-process" element={secured('dietech_Credit_Level', 'Credit Processing', <CreditAutoProcess />)} />

        {/* Clinical Nutrition */}
        <Route path="/clinical-nutrition" element={<SectionRedirect section="clinical" />} />
        <Route path="/nutritional" element={<SectionRedirect section="clinical" />} />
        <Route path="/clinical" element={<SectionRedirect section="clinical" />} />
        <Route path="/pdpm" element={secured('dietech_Pdpm_Level', 'Nutrition Compliance', <PdpmDashboard />)} />
        <Route path="/nutritional-analysis" element={secured('dietech_Analysis_Level', 'Nutritional Analysis', <NutritionalAnalysisEnhanced />)} />
        <Route path="/nutritional-validation" element={secured('dietech_Nutritional_Assessment_Level', 'Validation', <Validation />)} />
        <Route path="/intake-studies" element={secured('dietech_Intake_Study_Level', 'Intake Study', <IntakeStudies />)} />
        <Route path="/special-events" element={secured('dietech_Special_Event_Level', 'Special Events', <SpecialEventsPage />)} />
        <Route path="/special-events/:id" element={secured('dietech_Special_Event_Level', 'Special Events', <SpecialEventsPage />)} />
        <Route path="/tray-line-setup" element={secured('dietech_Tray_Line_Level', 'Tray Line Setup', <TrayLineSetup />)} />
        <Route path="/global-adjustments" element={secured('dietech_Global_Meal_Pattern_Adjustment_Level', 'Global Adjustments', <GlobalAdjustmentsPage />)} />
        <Route path="/global-adjustments/:id" element={secured('dietech_Global_Meal_Pattern_Adjustment_Level', 'Global Adjustments', <GlobalAdjustmentsPage />)} />
        <Route path="/meal-patterns" element={secured('dietech_Meal_Pattern_Level', 'Meal Pattern Groups', <MealPatterns />)} />
        {/* :id is one template: its detail screen on a phone, that row selected on a tablet or computer. */}
        <Route path="/global-meal-patterns" element={secured('dietech_Global_Meal_Pattern_Adjustment_Level', 'Global Meal Patterns', <GlobalMealPatternsPage />)} />
        <Route path="/global-meal-patterns/:id" element={secured('dietech_Global_Meal_Pattern_Adjustment_Level', 'Global Meal Patterns', <GlobalMealPatternsPage />)} />
        <Route path="/weight-entry" element={secured('dietech_Weight_History_Level', 'Weight Entry', <WeightEntry />)} />

        {/* Analysis & Reports */}
        {/* The seven Analysis screens are topics of one page; each keeps its address (/analysis/served-items). */}
        <Route path="/analysis" element={secured('dietech_Analysis_Level', 'Analysis', <AnalysisHome />)} />
        <Route path="/analysis/:topic" element={secured('dietech_Analysis_Level', 'Analysis', <AnalysisWorkbench />)} />
        <Route path="/analysis/nutritional" element={secured('dietech_Analysis_Level', 'Analysis', <NutritionalAnalysisEnhanced />)} />
        <Route path="/analysis/cost-menu" element={secured('dietech_Analysis_Level', 'Analysis', <CostAnalysisMenu />)} />
        <Route path="/analysis/cost-nourishment" element={secured('dietech_Analysis_Level', 'Analysis', <CostAnalysisNourishment />)} />
        <Route path="/analysis/demand-forecasting" element={secured('dietech_Analysis_Level', 'Analysis', <DemandForecasting />)} />
        <Route path="/reports/*" element={secured('dietech_Report_Level', 'Reports', <Reports />)} />
        <Route path="/workflows/*" element={secured('dietech_Level', 'Workflows', <Workflows />)} />

        {/* Clinical routes (hub merged into Clinical Nutrition) */}
        <Route path="/assessments" element={secured('dietech_Nutritional_Assessment_Level', 'Assessments', <AssessmentList />)} />
        <Route path="/assessments/new/:patientId" element={secured('dietech_Nutritional_Assessment_Level', 'Assessments', <AssessmentEditPage />)} />
        <Route path="/assessments/:id" element={secured('dietech_Nutritional_Assessment_Level', 'Assessments', <AssessmentWizardPage />)} />
        <Route path="/assessments/:id/edit" element={secured('dietech_Nutritional_Assessment_Level', 'Assessments', <AssessmentEditPage />)} />
        <Route path="/plan-of-care" element={secured('dietech_Plan_of_Care_Data_Entry_Level', 'Plan of Care', <PlanOfCareList />)} />
        <Route path="/plan-of-care/configuration" element={secured('dietech_Plan_of_Care_Backend_Tables_Level', 'Plan of Care Configuration', <PlanOfCareConfiguration />)} />
        <Route path="/plan-of-care/evaluation/:patientId/:headerId" element={secured('dietech_Plan_of_Care_Data_Entry_Level', 'Plan of Care', <PlanOfCareWizardPage />)} />
        <Route path="/plan-of-care/:id" element={secured('dietech_Plan_of_Care_Data_Entry_Level', 'Plan of Care', <PlanOfCareRedirect />)} />

        {/* Tools */}
        <Route path="/facilities" element={secured('dietech_Facility_Level', 'Facilities', <Facilities />)} />
        <Route path="/facilities/:id/units" element={secured('dietech_Facility_Level', 'Facilities', <FacilityUnits />)} />
        {/* Level 1 sees the settings read-only; changing them needs 2 (FacilitySettings.tsx). */}
        <Route path="/facilities/:id" element={secured('dietech_Facility_Level', 'Facility Settings', <FacilitySettings />)} />
        <Route path="/printers" element={secured('dietech_Facility_Level', 'Printers', <PrinterManagement />)} />
        {/* WinForms shows Ticket Print Schedule only above Facility level 1 (ucDietech_Facility.vb:331). */}
        <Route path="/print-schedules" element={secured('dietech_Facility_Level', 'Print Schedules', <PrintSchedules />, LEVEL.EDIT)} />
        <Route path="/unit-rooms" element={secured('dietech_Facility_Level', 'Unit Rooms', <UnitRooms />)} />
        <Route path="/census" element={secured('dietech_Facility_Level', 'Census', <FacilityCensus />)} />
        <Route path="/diet-config" element={secured('dietech_Diet_Type_Level', 'Diet Configuration', <DietConsistencyConfig />)} />
        {/* PSI staff only, on top of the USER_MGMT gate: it reports on the service across every
            customer, not on the signed-in facility. */}
        <Route path="/admin/status" element={secured('user_Level', 'System Status Admin', <PsiOnlyRoute feature="System Status Admin"><AdminStatusPage /></PsiOnlyRoute>, LEVEL.USER_MGMT)} />
        {/* Test Kitchen beta triage. PSI staff only, on top of the USER_MGMT gate: the queue spans
            every facility and findings carry screenshots of resident screens. */}
        <Route path="/admin/feedback" element={secured('user_Level', 'Test Kitchen', <PsiOnlyRoute feature="Test Kitchen"><FeedbackAdminPage /></PsiOnlyRoute>, LEVEL.USER_MGMT)} />
        {/* Getting Started (off unless GettingStarted:Enabled lists the connection; the page then shows Not Found).
            The page itself needs Facility level 2 or PSI staff, as the API does. */}
        {/* The allergy and food safety check (Getting Started B4) on its own: works with Getting Started off.
            Food Allergies table level 1, as AllergyProtectionController requires. */}
        <Route path="/allergy-protection" element={secured('dietech_Food_Allergies_Table_Level', 'Check allergy protection', <AllergyProtectionPage />, 1)} />
        <Route path="/getting-started/:step?" element={secured('dietech_Facility_Level', 'Getting Started', <GettingStartedPage />)} />
        <Route path="/settings/dietech-data-update" element={secured('dietech_Data_Update_Level', 'Dietech Data Update', <DietechDataUpdatePage />)} />
        {/* User administration is done in Tech Log II; here it is PSI staff only, as the API's
            user writes are. Anyone else is told where users are managed, whatever their level. */}
        <Route path="/users" element={usersPage(<UserList />)} />
        <Route path="/users/new" element={usersPage(<CreateUser />)} />
        <Route path="/users/deleted" element={usersPage(<DeletedUsers />)} />
        {/* Who is signed in: the desktop's Users Logged In right, or PSI staff (Jeff, 2026-11-03), as the
            API's GetLoggedIn. Not user administration, so not PSI only. */}
        <Route path="/users/logged-in" element={securedOrPsi('user_Users_Logged_In_Level', 'Logged In Users', <LoggedInUsers />)} />
        <Route path="/users/:id" element={usersPage(<UserDetail />)} />

        {/* Roles */}
        <Route path="/roles" element={secured('roles_Level', 'Roles', <RoleList />)} />
        <Route path="/roles/new" element={secured('roles_Level', 'Roles', <RoleDetail />)} />
        <Route path="/roles/:id" element={secured('roles_Level', 'Roles', <RoleDetail />)} />
        <Route path="/roles/:id/edit" element={secured('roles_Level', 'Roles', <RoleDetail />)} />
        <Route path="/roles/:id/usage" element={secured('roles_Level', 'Roles', <RoleUsage />)} />

        {/* EMR */}
        <Route path="/emr" element={secured('dietech_Ehr_Level', 'EMR/EHR', <EmrPage />)} />

        {/* User Settings */}
        <Route path="/user-settings" element={<UserSettings />} />

        {/* Setup's own page: the setup pages the role can open, as a plain list (Jeff, 2026-10-21:
            "it should be what it was before, with the multiple options of what to select"). The
            rail's Setup opens it. Application Settings (avatar menu) has no page; it lands here. */}
        <Route path="/settings" element={<SectionHome section="setup" />} />
        <Route path="/application-settings" element={<SectionRedirect section="setup" />} />
        {/* Kitchen's own page, the same list for the Kitchen pages (Jeff, 2026-10-23). Not /kitchen:
            that is the full-screen Kitchen Display. */}
        <Route path="/kitchen-home" element={<SectionHome section="kitchen" />} />

        {/* Purchasing. The web module lives under /ordering; /purchasing keeps the desktop's own
            lists (Suppliers, Departments, Ledgers), and any other /purchasing address, an old
            link or bookmark, goes to the same page under /ordering (Jeff, 2026-10-23). */}
        <Route path="/ordering" element={purchasingPage('pur_Level', 'Purchasing', <PurchasingHub />)} />
        <Route path="/purchasing" element={<Navigate to="/ordering" replace />} />
        <Route path="/purchasing/*" element={<MovedToOrdering />} />
        <Route path="/ordering/orders" element={purchasingPage('pUR_SO_Level', 'Purchase Orders', <PurchaseOrders />)} />
        <Route path="/ordering/orders/new" element={purchasingPage('pUR_SO_Level', 'Purchase Orders', <PurchaseOrderDetail />)} />
        <Route path="/ordering/orders/:id" element={purchasingPage('pUR_SO_Level', 'Purchase Orders', <PurchaseOrderDetail />)} />
        <Route path="/ordering/order-guides" element={purchasingPage('pUR_SO_Level', 'Order Guides', <OrderGuides />)} />
        <Route path="/ordering/reorder" element={purchasingPage('pUR_SO_Level', 'Reorder', <Reorder />, 'planning')} />
        <Route path="/ordering/requisitions" element={purchasingPage('pUR_SO_Level', 'Requisitions', <Requisitions />, 'planning')} />
        <Route path="/ordering/requisitions/new" element={purchasingPage('pUR_SO_Level', 'Requisitions', <RequisitionDetail />, 'planning')} />
        <Route path="/ordering/requisitions/:id" element={purchasingPage('pUR_SO_Level', 'Requisitions', <RequisitionDetail />, 'planning')} />
        <Route path="/ordering/demand" element={purchasingPage('pUR_SO_Level', 'Demand Planning', <DemandPlanning />, 'planning')} />
        <Route path="/ordering/receiving" element={purchasingPage('pUR_Inv_Level', 'Receiving', <Receiving />, 'receiving')} />
        <Route path="/ordering/receiving/:poId" element={purchasingPage('pUR_Inv_Level', 'Receiving', <ReceivingDetail />, 'receiving')} />
        <Route path="/ordering/invoices" element={purchasingPage('pUR_Inv_Level', 'Vendor Invoices', <VendorInvoices />, 'receiving')} />
        <Route path="/ordering/invoices/new" element={purchasingPage('pUR_Inv_Level', 'Vendor Invoices', <VendorInvoiceDetail />, 'receiving')} />
        <Route path="/ordering/invoices/:id" element={purchasingPage('pUR_Inv_Level', 'Vendor Invoices', <VendorInvoiceDetail />, 'receiving')} />
        <Route path="/ordering/credit-memos" element={purchasingPage('pUR_Inv_Level', 'Credit Memos', <CreditMemos />, 'receiving')} />
        <Route path="/ordering/credit-memos/new" element={purchasingPage('pUR_Inv_Level', 'Credit Memos', <CreditMemoDetail />, 'receiving')} />
        <Route path="/ordering/credit-memos/:id" element={purchasingPage('pUR_Inv_Level', 'Credit Memos', <CreditMemoDetail />, 'receiving')} />
        <Route path="/ordering/inventory" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryDashboard />, 'inventory')} />
        <Route path="/ordering/inventory/items" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryItems />, 'inventory')} />
        <Route path="/ordering/inventory/items/new" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryItemDetail />, 'inventory')} />
        <Route path="/ordering/inventory/items/:id" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryItemDetail />, 'inventory')} />
        <Route path="/ordering/inventory/transactions" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryTransactions />, 'inventory')} />
        <Route path="/ordering/inventory/counts" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryCounts />, 'inventory')} />
        <Route path="/ordering/inventory/counts/:id" element={purchasingPage('pUR_Inv_Level', 'Inventory', <InventoryCountDetail />, 'inventory')} />
        <Route path="/ordering/inventory/floor-stock" element={purchasingPage('pUR_Inv_Level', 'Inventory', <FloorStockPage />, 'inventory')} />
        <Route path="/ordering/inventory-planning" element={purchasingPage('pur_Level', 'Inventory Planning', <InventoryPlanning />, 'inventory')} />
        <Route path="/ordering/waste" element={purchasingPage('pUR_Inv_Level', 'Waste', <Waste />, 'inventory')} />
        <Route path="/ordering/suppliers" element={purchasingPage('pur_Level', 'Suppliers', <Suppliers />)} />
        <Route path="/ordering/suppliers/new" element={purchasingPage('pur_Level', 'Suppliers', <SupplierDetail />)} />
        <Route path="/ordering/suppliers/:id" element={purchasingPage('pur_Level', 'Suppliers', <SupplierDetail />)} />
        <Route path="/ordering/item-links" element={purchasingPage('pur_Level', 'Item Links', <ItemLinks />, ['planning', 'analytics'])} />
        <Route path="/ordering/catalog" element={purchasingPage('pur_Level', 'Price Updates', <PriceUpdates />)} />
        <Route path="/ordering/contracts" element={purchasingPage('pur_Level', 'Contracts', <Contracts />, 'contractsEdi')} />
        <Route path="/ordering/contracts/new" element={purchasingPage('pur_Level', 'Contracts', <ContractDetail />, 'contractsEdi')} />
        <Route path="/ordering/contracts/:id" element={purchasingPage('pur_Level', 'Contracts', <ContractDetail />, 'contractsEdi')} />
        <Route path="/ordering/pricing-import" element={purchasingPage('pur_Level', 'Price Updates', <PriceUpdates />)} />
        <Route path="/ordering/contacts" element={purchasingPage('pur_Level', 'Contacts', <ContactManager />)} />
        <Route path="/ordering/edi" element={purchasingPage('pur_Level', 'EDI', <EdiTransactionLog />, 'contractsEdi')} />
        <Route path="/ordering/costing" element={purchasingPage('pur_Level', 'Costing', <Costing />, 'analytics')} />
        <Route path="/ordering/prep-schedule" element={purchasingPage('pur_Level', 'Prep Schedule', <PrepSchedule />, 'analytics')} />
        <Route path="/ordering/allergens" element={purchasingPage('pur_Level', 'Allergens', <AllergenExposure />, 'analytics')} />
        <Route path="/ordering/spend" element={purchasingPage('pur_Level', 'Spend', <SpendAnalytics />)} />
        {/* The desktop's Order Forms > Inventory > Meal Cost Summary, with Inventory like the rest of it. */}
        <Route path="/ordering/meal-cost" element={purchasingPage('pUR_Inv_Level', 'Meal Cost', <MealCost />, 'inventory')} />
        <Route path="/ordering/scorecard" element={purchasingPage('pur_Level', 'Supplier Scorecard', <SupplierScorecard />, 'analytics')} />
        <Route path="/ordering/corporate" element={purchasingPage('pur_Level', 'Corporate Purchasing', <CorporatePurchasing />, 'analytics')} />
        <Route path="/ordering/setup" element={purchasingPage('pUR_Tables_Level', 'Purchasing Setup', <PurchasingSetup />)} />
        {/* Not a `purchasingPage`: WinForms puts Departments in the purchasing
            tree on the role level alone (frmDietech_Resident_DataGrid_V2.vb:6893),
            and Pur_Allow is a web-only licence for the newer module, so gating
            on it would hide a page the desktop shows. */}
        <Route path="/purchasing/departments" element={secured('dietech_Purchasing_Department_Level', 'Departments', <OPDepartments />)} />
        <Route path="/purchasing/ledgers" element={secured('dietech_Purchasing_Ledger_Level', 'Ledgers', <OPLedgers />)} />

        {/* Satisfaction Surveys */}
        <Route path="/surveys" element={secured('dietech_Survey_Level', 'Surveys', <SurveyTemplates />)} />
        <Route path="/surveys/templates/new" element={secured('dietech_Survey_Level', 'Surveys', <SurveyTemplateDetail />)} />
        <Route path="/surveys/templates/:id" element={secured('dietech_Survey_Level', 'Surveys', <SurveyTemplateDetail />)} />
        <Route path="/surveys/sessions" element={secured('dietech_Survey_Level', 'Surveys', <SurveySessions />)} />
        <Route path="/surveys/sessions/new" element={secured('dietech_Survey_Level', 'Surveys', <SurveySessionDetail />)} />
        <Route path="/surveys/sessions/:id" element={secured('dietech_Survey_Level', 'Surveys', <SurveySessionDetail />)} />
        <Route path="/surveys/sessions/:id/collect" element={secured('dietech_Survey_Level', 'Surveys', <SurveyResponseEntry />)} />
        <Route path="/surveys/analysis" element={secured('dietech_Survey_Level', 'Surveys', <SurveyAnalysis />)} />

        {/* Selective Touch - wildcard for sub-routes (patient, review, queue, etc.) */}
        <Route path="/selective-touch/*" element={secured('dietech_Selective_Menu_Touch_Level', 'Selective Touch', <SelectiveTouch />)} />

        {/* System Log */}
        <Route path="/system-log" element={secured('dietech_Interaction_Logging_Level', 'System Log', <SystemInteractionLog />)} />

        {/* Help */}
        <Route path="/help" element={<HelpPage />} />
        {/* One help article; the list and the page help live on /help (help/pageHelp.ts). */}
        <Route path="/help/:slug" element={<HelpArticlePage />} />

        {/* PSI staff only (psibuffalo.com login); not in the sidebar */}

        {/* 404 - inside MainLayout so sidebar/nav are visible */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Kitchen Hub - full-screen, orders + deliveries */}
      <Route path="/kitchen" element={secured('dietech_Kitchen_Display_Level', 'Kitchen', <KitchenHub />)} />

      {/* FHIR Integration - public pages, no auth */}
      <Route path="/fhir" element={<FhirInfo />} />
      <Route path="/fhir/terms" element={<FhirTerms />} />
      <Route path="/terms" element={<FhirTerms />} />
      <Route path="/smart-callback" element={<SmartCallback />} />
      <Route path="/launch" element={<SmartLaunch />} />
      <Route path="/smart-launch" element={<SmartLaunch />} />

      {/* Patient Self-Order - separate layout, SelfOrder JWT auth (not Keycloak) */}
      <Route path="/self-order" element={<SelfOrderApp />} />
      {/* Registered Selective Touch tablet: device token + server PIN, no Keycloak */}
      <Route path="/tablet/*" element={<TabletApp />} />

      {/* Bedside TV / Pillow Speaker - separate layout, no sidebar, no auth */}
      <Route path="/bedside" element={<BedsideLayout />}>
        <Route index element={<BedsideScreensaver />} />
        <Route path="activate/:deviceToken" element={<BedsideActivation />} />
        <Route path="pin" element={<BedsidePin />} />
        <Route path="welcome" element={<BedsideWelcome />} />
        <Route path="menu" element={<BedsideMenuView />} />
        <Route path="select" element={<BedsideMenuSelect />} />
        <Route path="confirm" element={<BedsideConfirmation />} />
        <Route path="status" element={<BedsideOrderStatus />} />
        <Route path="tracking" element={<BedsideTracking />} />
        <Route path="rate" element={<BedsideRating />} />
        <Route path="diag" element={<BedsideDiagnostics />} />
        <Route path="snack" element={<BedsideSnackMenu />} />
        <Route path="guest-tray" element={<BedsideGuestTray />} />
      </Route>

      {/* Default landing — driven by the current user's
          `tblPMSec_User.DF_Start_Form` (mirrors the WinForms
          "Startup Form" preference):
             2 → Selective Touch
             6 → Dashboard
             anything else (incl. 0/1) → main patient grid.
          Waits for the user record to arrive before redirecting so
          we don't briefly land on the fallback and then hop. First the
          login check: a database that is behind is updated before anyone
          goes in (DatabaseUpdateGate). */}
      <Route path="/" element={<DatabaseUpdateGate><StartFormRedirect /></DatabaseUpdateGate>} />
    </Routes>
    </Suspense>
  );
}

function ThemedApp() {
  const { darkMode, themeSize } = useThemeStore();
  const ui2026 = useThemeStore((s) => s.ui2026);
  const muiTheme = useMemo(
    () => buildMuiTheme(darkMode, themeSize, ui2026),
    [darkMode, themeSize, ui2026],
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <BrowserRouter>
        {/* Records the page each navigation came from; the Residents grid uses
            it to tell coming back from a fresh visit. */}
        <RouteOriginTracker />
        {/* Loads a waiting new build on the next move to another section. */}
        <AppUpdateOnNavigation />
        <AuthGuard>
          <ReportViewerProvider>
            <ReportAutoOpenerProvider>
              {/* Inside the Router: it reads useLocation(). Mounted next to
                  LiveAnnouncer originally, which is outside — that type-checks
                  and throws on first render. */}
              <PageAnnouncer />
              {/* One page view per route, signed-in pages only. */}
              <PageViewTracker />
              <ScreenSummaryAnnouncer />
              <AppRouter />
            </ReportAutoOpenerProvider>
          </ReportViewerProvider>
        </AuthGuard>
        {/* Inside the Router so a toast's button can open a page (the Print Queue
            after a report is queued). Outside AuthGuard, as before, so a toast
            still shows on the login and connection screens. */}
        <NotificationToast />
      </BrowserRouter>
      {/* Speaks every toast to a screen reader. Mounted beside the visual
          toast rather than inside it so the announcement does not depend on
          the toast's own render or dismissal timing. */}
      <LiveAnnouncer />
      <ApiDownOverlay />
      <FacilitySwitchOverlay />
      <SessionExpiredDialog />
      <VersionMismatchDialog />
      <SessionExpiryWarning />
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider userManager={getUserManager()} onSigninCallback={onSigninCallback}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary level="app" label="Root">
          <ItemSavedListener />
          <ThemedApp />
        </ErrorBoundary>
      </QueryClientProvider>
    </AuthProvider>
  );
}
